import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@NA/Overrides/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";

import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { formatPrice } from "@NA/Utils/Utility";
import { CartSummaryMethodsResults } from "@NA/Services/ADICartSummaryService";
import React, { Fragment, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import setUpdateCartLoading from "@NA/Handlers/ADIUpdateCartLoadingHandler";
import { useDispatch } from "react-redux";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    TaxAndShippingDtls?: CartSummaryMethodsResults;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId, isPlacingOrder, isCheckingOutWithPayPay } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const deliveryDescription = cartState.value?.properties?.deliveryDescription;
    const slicedUPS = deliveryDescription?.slice(deliveryDescription.indexOf("-") + 2);
    const matchingDeliveryMethod = state.ADIGlobalData.shippingMethodStore.shippingMethods?.deliveryMethod.filter(
        item => {
            return item.descr.trim() === slicedUPS;
        },
    );

    const TaxAndShippingDtls = state.ADIGlobalData.TaxSummaryStore?.CartSummaryMethods;
    let isTBDStatus;
    if (matchingDeliveryMethod?.length) {
        isTBDStatus = matchingDeliveryMethod[0].isTBD;
    }
    return {
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        isDisabled: state.ADIGlobalData.ADIUpdateCartLoading.loading,
        isTBD: isTBDStatus,
        currentPage: getCurrentPage(state),
        TaxAndShippingDtls: TaxAndShippingDtls,
        cartState: cartState,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    newlabelGridItem?: GridItemProps;
    newvalueGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalContainer?: InjectableCss;
    subtotalContainerSavings?: InjectableCss;
    subtotalContainerMaterialTotal?: InjectableCss;
    promocodeStyles?: InjectableCss;
    subtotalValue?: TypographyProps;
    promoSubtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    promoTotalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            padding: 20px 0px 0px;
            display: flex;
            margin-bottom: 30px;
            justify-content: end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 28px;
                        padding: 20px 0px 0px;
                        justify-content: start;
                        > ${GridOffset} {
                            margin: 0px;
                            > ${GridItemStyle} {
                                padding-left: 0px;
                                padding-right: 0px;
                            }
                        }
                    `,
                    css`
                        margin-bottom: 28px;
                        padding: 20px 0px 0px;
                        justify-content: start;
                        > ${GridOffset} {
                            margin: 0px;
                            > ${GridItemStyle} {
                                padding-left: 0px;
                                padding-right: 0px;
                            }
                        }
                    `,
                    css`
                        margin-bottom: 31px;
                    `,
                    null,
                    null,
                ])}
            > div[class^="GridOffset"] {
                width: calc(100% - 322px + 23px);
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: calc(100% - 299px);
                        `,
                        css`
                            width: calc(100% - 299px);
                        `,
                        css`
                            width: calc(100% - 299px);
                        `,
                    ])}
            }
        `,
    },
    promocodeStyles: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: left;
            color: ${getColor("success")};
            padding-bottom: 0;
        `,
    },
    promoTotalLabel: {
        css: css`
            font-weight: bold;
            padding-bottom: 0px;
            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
        `,
    },
    subtotalLabel: {
        css: css`
            font-weight: bold;
            padding-bottom: 5px;
            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
        `,
    },
    totalLabel: {
        css: css`
            font-weight: bold;
            padding-bottom: 5px;

            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            color: ${getColor("text.main")};
        `,
    },
    totalValue: {
        css: css`
            font-weight: bold;
            padding-bottom: 5px;

            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            margin-left: auto;
        `,
    },
    discountsLabel: {
        css: css`
            padding-bottom: 5px;

            font-size: 14px;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            margin-left: auto;
        `,
    },
    discountsValue: {
        css: css`
            padding-bottom: 5px;

            font-size: 14px;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            margin-left: auto;
        `,
    },
    subtotalContainer: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            justify-content: space-between;
            padding-bottom: 0;
        `,
    },
    subtotalContainerSavings: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            justify-content: space-between;
            padding-bottom: 0;
            padding-top: 0px;
            margin-bottom: 10px !important;
        `,
    },
    subtotalContainerMaterialTotal: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            justify-content: space-between;
            padding-bottom: 0;
            border-top: 1px solid ${getColor("common.border")} !important;
        `,
    },
    newlabelGridItem: {
        css: css`
            flex-basis: auto;
            max-width: max-content;
            padding: 0;
        `,
    },
    newvalueGridItem: {
        css: css`
            flex-basis: auto;
            max-width: max-content;
            padding: 0;
        `,
    },
    labelGridItem: {
        css: css`
            flex-basis: auto;
            max-width: max-content;
            padding: 0;
        `,
    },
    valueGridItem: {
        css: css`
            flex-basis: auto;
            max-width: max-content;
            padding: 0;
        `,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
            font-weight: bold;
            padding-bottom: 5px;
            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            word-break: keep-all;
        `,
    },
    promoSubtotalValue: {
        css: css`
            margin-left: auto;
            font-weight: bold;
            padding-bottom: 0px;
            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            word-break: keep-all;
        `,
    },
    shippingAndHandlingLabel: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
        `,
    },
    promotionLabel: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("success")};
            font-weight: 600;
        `,
    },
    promotionValue: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("success")};
            margin-left: auto;
            font-weight: 600;
        `,
    },
    taxLabel: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
        `,
    },
    taxValue: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            margin-left: auto;
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
};

const ADICheckoutCartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    extendedStyles,
    isDisabled,
    isTBD,
    currentPage,
    TaxAndShippingDtls,
    cartState,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch({
            type: "Custom/Checkout/LoadCheckoutCart",
        });
    }, []);
    if (!cart) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping ? (
                    <>
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel}>{translate("Total:")}</Typography>
                            </GridItem>
                        </GridItem>
                    </>
                ) : (
                    <>
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.newlabelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Material Total:") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                </Typography>
                            </GridItem>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    if (isCartEmpty) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel}>{translate("Total:")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
                            </GridItem>
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Material Total:") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                            </GridItem>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    // const displayTaxAndVatPrice = () => {
    //     if (cart && cart.totalTaxDisplay && cart.totalTaxDisplay != undefined) {
    //         return `${cart.totalTaxDisplay}`;
    //     } else {
    //         return "0";
    //     }
    // };

    const displayShippingtax = () => {
        if (
            cart &&
            cart.properties["freightCharge"] &&
            cart.properties["freightCharge"] != undefined &&
            cart.properties["freightCharge"] !== "0.00"
        ) {
            return `${formatPrice(`${cart.properties["freightCharge"]}`)}`;
        } else {
            return "0.00";
        }
    };

    //method to calculate total tax -----P21 Change
    const displayTotaltax = () => {
        if (
            cart &&
            cart.properties["totalTax"] &&
            cart.properties["totalTax"] !== "0.00" &&
            cart.properties["totalTax"] !== undefined &&
            !Number.isNaN(Number(cart.properties["totalTax"]))
        ) {
            return `${formatPrice(`${cart.properties["totalTax"]}`)}`;
        } else {
            return "0.00";
        }
    };

    const isCartPage = currentPage.type === "CartPage";

    var orderSubTotalDisplay = cart.orderSubTotalDisplay;
    if (
        cart.properties["isParentPromoList"] &&
        cart.properties["isParentPromoList"] === "true" &&
        cart.properties["parentChildList"] &&
        cart.properties["parentChildList"] !== ""
    ) {
        const discount = Number(cart.properties["customDiscountAmount"]) || 0;
        const coupon = Number(cart.properties["childCouponDiscnt"]) || 0;
        const total = cart.orderSubTotal - discount + coupon;
        orderSubTotalDisplay = total.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
        });
    }

    const materialTotalLabel =
        cart.properties["freeShipPromocode"] !== "" || cart.properties["customerPromocode"] !== ""
            ? styles.promoTotalLabel
            : styles.subtotalLabel;
    const materialValueLabel =
        cart.properties["freeShipPromocode"] !== "" || cart.properties["customerPromocode"] !== ""
            ? styles.promoSubtotalValue
            : styles.subtotalValue;

    const calculateTotal = () => {
        setUpdateCartLoading({ loading: true });

        const finalGrandTotal = parseFloat(cart.properties["finalGrandTotal"]) || 0;
        const ecoFee = cart.totalEcoFeeDisplay ? parseFloat(cart.totalEcoFeeDisplay.split("$")[1]) || 0 : 0;
        const specialHandlingFee = cart.totalSpecialHandlingFeeDisplay
            ? parseFloat(cart.totalSpecialHandlingFeeDisplay.split("$")[1]) || 0
            : 0;

        const total = finalGrandTotal + ecoFee + specialHandlingFee;

        if (!isNaN(total)) {
            setUpdateCartLoading({ loading: false });
        }

        return total;
    };
    // let finalTotalIncludingTax = 0;

    // if ((showTaxAndShipping && !isTBD) || cart?.totalSpecialHandlingFee != 0 || cart?.totalEcoFee != 0) {
    //     finalTotalIncludingTax = calculateTotal();
    // }
    return (
        <LoadingOverlay loading={isDisabled}>
            <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
                {(showTaxAndShipping || !isCartPage) && (
                    <GridItem {...styles.subtotalContainer}>
                        <GridItem {...styles.newlabelGridItem}>
                            <Typography {...materialTotalLabel}>
                                {translate("Material Total:") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.newvalueGridItem}>
                            <Typography {...materialValueLabel} data-test-selector="cartTotal_subTotal">
                                {cart.properties.salePriceItems === ""
                                    ? orderSubTotalDisplay
                                    : cart.orderGrandTotalDisplay}
                            </Typography>
                        </GridItem>
                    </GridItem>
                )}

                {cart.properties["customerPromocode"] &&
                    cart.properties["customerPromocode"] !== "" &&
                    cart.properties["customerPromocode"] !== "180Unlimited" &&
                    cart.properties.isParentPromoList === "false" && (
                        <>
                            <GridItem {...styles.subtotalContainerSavings}>
                                <GridItem {...styles.labelGridItem}>
                                    {cart.properties.freeQuantity <= "0" && cart.properties.salePriceItems === "" && (
                                        <StyledWrapper {...styles.promocodeStyles}>
                                            {translate("Savings:")}
                                        </StyledWrapper>
                                    )}
                                    {cart.properties.freeQuantity <= "0" && cart.properties.salePriceItems !== "" && (
                                        <StyledWrapper {...styles.promocodeStyles}>
                                            {translate("Total Promo Savings:")}{" "}
                                            {cart.properties["customDiscountAmountDisplay"]}
                                        </StyledWrapper>
                                    )}
                                    {cart.properties.freeQuantity > "0" && (
                                        <StyledWrapper {...styles.promocodeStyles}>
                                            {translate("Free Item")}
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    {cart.properties.freeQuantity <= "0" && cart.properties.salePriceItems === "" && (
                                        <StyledWrapper {...styles.promocodeStyles}>
                                            -{cart.properties["customDiscountAmountDisplay"]}
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            </GridItem>
                        </>
                    )}
                {cart.properties["customerPromocode"] &&
                    cart.properties["customerPromocode"] !== "" &&
                    cart.properties["customerPromocode"] !== "180Unlimited" &&
                    cart.properties.isParentPromoList === "true" &&
                    cart.properties.childCouponDiscnt > "0" && (
                        <>
                            <GridItem {...styles.subtotalContainer} style={{ paddingBottom: "5px" }}>
                                <GridItem {...styles.labelGridItem}>
                                    {cart.properties.childCouponDiscnt !== "" && (
                                        <StyledWrapper {...styles.promocodeStyles}>
                                            {translate("Additional Savings:")} {cart.properties["customerPromocode"]}
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    {cart.properties.childCouponDiscnt !== "" && (
                                        <StyledWrapper {...styles.promocodeStyles}>
                                            -${cart.properties["childCouponDiscnt"]}
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            </GridItem>
                        </>
                    )}

                {cart.properties["customerPromocode"] &&
                    cart.properties["customerPromocode"] !== "" &&
                    cart.properties["customerPromocode"] !== "180Unlimited" &&
                    cart.properties.freeQuantity <= "0" &&
                    cart.properties.salePriceItems === "" &&
                    cart.properties.isParentPromoList !== "true" && (
                        <>
                            <GridItem {...styles.subtotalContainerMaterialTotal}>
                                <GridItem {...styles.newlabelGridItem}>
                                    <Typography {...styles.subtotalLabel}>
                                        {translate("Material Total:") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.newvalueGridItem}>
                                    <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                                        {cart.orderGrandTotalDisplay}
                                    </Typography>
                                </GridItem>
                            </GridItem>
                        </>
                    )}
                {cart.properties["freeShipPromocode"] && (
                    <GridItem {...styles.subtotalContainer}>
                        <GridItem {...styles.labelGridItem}>
                            <StyledWrapper {...styles.promocodeStyles}>{translate("Free Shipping")}</StyledWrapper>
                        </GridItem>
                    </GridItem>
                )}
                {cart.properties["customerPromocode"] &&
                    cart.properties["customerPromocode"] !== "" &&
                    cart.properties["customerPromocode"] !== "180Unlimited" &&
                    cart.properties.isParentPromoList === "true" &&
                    cart.properties.childCouponDiscnt > "0" && (
                        <>
                            <GridItem {...styles.subtotalContainerMaterialTotal}>
                                <GridItem {...styles.newlabelGridItem}>
                                    <Typography {...styles.subtotalLabel}>
                                        {translate("Material Total:") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.newvalueGridItem}>
                                    <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                                        {cart.orderGrandTotalDisplay}
                                    </Typography>
                                </GridItem>
                            </GridItem>
                        </>
                    )}

                {orderPromotions &&
                    orderPromotions.map(promotion => (
                        <Fragment key={promotion.id}>
                            <GridItem {...styles.subtotalContainer}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography
                                        {...styles.promotionLabel}
                                        data-test-selector="cartTotalOrderPromotionName"
                                    >
                                        {`${translate("Promotion")}: ${promotion.name}`}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography
                                        {...styles.promotionValue}
                                        data-test-selector="cartTotalOrderPromotionValue"
                                    >
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                            </GridItem>
                        </Fragment>
                    ))}

                {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && !isTBD && (
                    <>
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping:")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.shippingAndHandlingValue}
                                    data-test-selector="cartTotalShippingAndHandlingAmount"
                                >
                                    {/* {cart.properties["isPickUp"] === "Y"
                                    ? `${cart.currencySymbol}0`
                                    :`${cart.currencySymbol}${cart.properties["freightCost"]?.trim()}`}*/}
                                    {/* {cart.shippingAndHandlingDisplay} */}
                                    {`${cart.currencySymbol}`}
                                    {displayShippingtax()}
                                </Typography>
                            </GridItem>
                        </GridItem>
                    </>
                )}
                {shippingPromotions &&
                    shippingPromotions.map(promotion => (
                        <Fragment key={promotion.id}>
                            <GridItem {...styles.subtotalContainer}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.promotionLabel}>
                                        {`${translate("Promotion")}: ${promotion.name}`}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography
                                        {...styles.promotionValue}
                                        data-test-selector="cartTotalShippingPromotionValue"
                                    >
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                            </GridItem>
                        </Fragment>
                    ))}
                {/* cart values display properties --- P21 Change */}
                {/* {cart.properties["freeShipPromocode"] && (
                    <GridItem {...styles.subtotalContainer}>
                        <GridItem {...styles.labelGridItem}>
                            <StyledWrapper {...styles.promocodeStyles}>{translate("Free Shipping")}</StyledWrapper>
                        </GridItem>
                    </GridItem>
                )} */}
                {cart.totalTaxDisplay.length > 0 &&
                    cart.customerOrderTaxes!.length === 0 &&
                    cart.totalEcoFeeDisplay != "$0.00" && (
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.taxLabel}>{translate("Eco Fee:")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                    {cart?.totalEcoFeeDisplay}
                                </Typography>
                            </GridItem>
                        </GridItem>
                    )}
                {cart.totalTaxDisplay.length > 0 &&
                    cart.customerOrderTaxes!.length === 0 &&
                    cart?.totalSpecialHandlingFeeDisplay != "$0.00" && (
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.taxLabel}>{translate("Special Handling Fee:")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                    {cart?.totalSpecialHandlingFeeDisplay}
                                </Typography>
                            </GridItem>
                        </GridItem>
                    )}
                {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                    <GridItem {...styles.subtotalContainer}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.taxLabel}>
                                {enableVat ? translate("VAT:") : translate("Tax:")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                {`${cart.currencySymbol}`}
                                {displayTotaltax()}
                            </Typography>
                        </GridItem>
                    </GridItem>
                )}
                {showTaxAndShipping &&
                    cart.customerOrderTaxes!.map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.subtotalContainer}>
                                    <GridItem {...styles.labelGridItem}>
                                        <Typography {...styles.taxLabel}>
                                            {tax.taxDescription || translate("Tax:")}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.valueGridItem}>
                                        {/* <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography> */}
                                        <Typography {...styles.taxValue}>
                                            {`${cart.currencySymbol}`}
                                            {displayTotaltax()}
                                        </Typography>
                                    </GridItem>
                                </GridItem>
                            </Fragment>
                        );
                    })}
                {/* {((showTaxAndShipping && !isTBD) || (cart?.specialHandlingChargelist?.length != 0)) &&  */}
                {((showTaxAndShipping && !isTBD) || cart?.totalSpecialHandlingFee != 0 || cart?.totalEcoFee != 0) && (
                    <>
                        <GridItem {...styles.subtotalContainer}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel}>{translate("Total:")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                {!isTBD && !isDisabled && (
                                    <Typography
                                        {...styles.totalValue}
                                        data-test-selector="cartTotalOrderGrandTotalDisplay"
                                    >
                                        {`$${calculateTotal().toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}`}
                                    </Typography>
                                )}
                                {isTBD && (
                                    <Typography
                                        {...styles.totalValue}
                                        data-test-selector="cartTotalOrderGrandTotalDisplay"
                                    >
                                        $
                                        {cartState?.isLoading || isNaN(Number(cart.finalGrandTotal))
                                            ? 0
                                            : Number(
                                                  cart.orderGrandTotal +
                                                      Number(cart.totalEcoFeeDisplay?.split("$")[1] || 0) +
                                                      (cart?.totalSpecialHandlingFee || 0),
                                              ).toLocaleString("en-US", {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                              })}
                                    </Typography>
                                )}
                            </GridItem>
                        </GridItem>
                    </>
                )}
                {discountTotal !== undefined && discountTotal > 0 && (
                    <GridItem {...styles.subtotalContainer}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.discountsValue}>
                                {`${translate("You saved")} `}{" "}
                                <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                            </Typography>
                        </GridItem>
                    </GridItem>
                )}
            </GridContainer>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps)(ADICheckoutCartTotalDisplay);
